// /src/pages/UpsertCounter.jsx
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaBoxes } from "react-icons/fa";
import toast from "react-hot-toast";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import {
  useAddCounterMutation,
  useUpdateCounterMutation,
  useGetCounterByIdQuery,
} from "../toolkitServices/counterApi";

/* ---------- Shared UI tokens (consistent with CreateCategory etc.) ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const UpsertCounter = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const mode = id ? "edit" : "create";

  const [step, setStep] = useState(1);

  // Branches for dropdown
  const {
    data: branchesResp,
    isLoading: isBranchesLoading,
    error: branchesError,
  } = useGetAllBranchesQuery({ page: 1, limit: 100, q: "", active: true });

  const branches =
    branchesResp?.data || branchesResp?.items || branchesResp?.rows || [];

  // Existing (edit)
  const {
    data: counterResp,
    isFetching: isCounterLoading,
    error: counterError,
  } = useGetCounterByIdQuery(id, { skip: !id });
  const existing = counterResp?.data || counterResp || null;

  // Mutations
  const [addCounter, { isLoading: isSavingCreate }] = useAddCounterMutation();
  const [updateCounter, { isLoading: isSavingUpdate }] =
    useUpdateCounterMutation();
  const isSaving = isSavingCreate || isSavingUpdate;

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
    watch,
    setFocus,
  } = useForm({
    defaultValues: {
      branchId: "",
      counterName: "",
      counterCode: "",
      description: "",
      isActive: true,
    },
    mode: "onBlur",
  });

  // Prefill on edit
  useEffect(() => {
    if (mode === "edit" && existing) {
      reset({
        branchId: existing.branchId || existing.branch?._id || "",
        counterName: existing.counterName || "",
        counterCode: existing.counterCode || "",
        description: existing.description || "",
        isActive:
          typeof existing.isActive === "boolean" ? existing.isActive : true,
      });
      setStep(1);
      requestAnimationFrame(() => setFocus("branchId"));
    }
  }, [mode, existing, reset, setFocus]);

  // Load error
  useEffect(() => {
    if (counterError && mode === "edit") {
      toast.error("Failed to load counter details");
    }
  }, [counterError, mode]);

  // Keyboard shortcuts: Ctrl/⌘+S save/next, Ctrl/⌘+Backspace back, Alt+← prev
  const nextStep = useCallback(async () => {
    const valid = await trigger();
    if (valid) setStep((s) => (s < 2 ? s + 1 : s));
  }, [trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 2) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, handleSubmit, navigate, nextStep, prevStep]);

  // Auto-focus on mount/step change
  useEffect(() => {
    const idRAF = requestAnimationFrame(() => {
      if (step === 1) setFocus("branchId");
      if (step === 2) setFocus("description");
    });
    return () => cancelAnimationFrame(idRAF);
  }, [step, setFocus]);

  // Submit (create/update)
  const onSubmit = async (form) => {
    const payload = {
      branchId: form.branchId,
      counterName: (form.counterName || "").trim(),
      counterCode: (form.counterCode || "").trim(),
      description: (form.description || "").trim(),
      // Note: API can ignore isActive on create if not supported
      isActive: !!form.isActive,
    };

    try {
      if (mode === "edit") {
        await updateCounter({ id, ...payload }).unwrap();
        toast.success("Counter updated!");
        // stay on page
      } else {
        await addCounter(payload).unwrap();
        toast.success("Counter created!");
        reset({
          branchId: "",
          counterName: "",
          counterCode: "",
          description: "",
          isActive: true,
        });
        setStep(1);
        requestAnimationFrame(() => setFocus("branchId"));
      }
    } catch (e) {
      const msg =
        e?.data?.msg ||
        e?.data?.message ||
        e?.error ||
        (mode === "edit" ? "Update failed!" : "Create failed!");
      toast.error(msg);
    }
  };

  const counterName = watch("counterName");
  const pageTitle = mode === "edit" ? "Edit Counter" : "Create Counter";
  const loading = isSaving || isSubmitting || (mode === "edit" && isCounterLoading);

  return (
    <div className="px-4 py-4">
      {/* Header (compact, aligned with other Create/Edit screens) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/organisation/counter-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Counter List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                  {pageTitle}
                </h1>
                <p className="text-xs text-gray-500">
                  Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">S</kbd> save/next,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">Ctrl</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> back,{" "}
                  <kbd className="px-1 bg-gray-100 rounded border ml-1">Alt</kbd>+
                  <kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev step
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 pr-2">
                <FaBoxes className="text-gray-400" />
                <span className="text-xs text-gray-500">Step {step} of 2</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card — compact with loading overlay and border-only validation */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() =>
          requestAnimationFrame(() =>
            setFocus(step === 1 ? "branchId" : "description")
          )
        }
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form
          className="p-5"
          onSubmit={(e) => e.preventDefault()} // submit via buttons/Ctrl+S
        >
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Branch */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Branch <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("branchId", { required: "Branch is required" })}
                  aria-invalid={!!errors.branchId}
                  disabled={isBranchesLoading || branchesError}
                  className={`${baseInput} ${errors.branchId ? errRing : okRing}`}
                >
                  <option value="">
                    {isBranchesLoading
                      ? "Loading branches…"
                      : branchesError
                      ? "Failed to load branches"
                      : "Select branch"}
                  </option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.branchName || b.name}
                    </option>
                  ))}
                </select>
                {errors.branchId?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.branchId.message}
                  </p>
                )}
              </div>

              {/* Counter Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Counter Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("counterName", {
                    required: "Counter name is required",
                    validate: (v) =>
                      (v || "").trim().length >= 2 || "At least 2 characters",
                  })}
                  aria-invalid={!!errors.counterName}
                  placeholder="e.g. Gold-Showcase-1"
                  autoComplete="off"
                  className={`${baseInput} ${
                    errors.counterName ? errRing : okRing
                  }`}
                />
                {errors.counterName?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.counterName.message}
                  </p>
                )}
              </div>

              {/* Counter Code (optional) */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Counter Code
                </label>
                <input
                  {...register("counterCode", {
                    validate: (v) =>
                      !v ||
                      /^[a-zA-Z0-9-_]+$/.test(v) ||
                      "Only A–Z, 0–9, - and _",
                  })}
                  aria-invalid={!!errors.counterCode}
                  placeholder="e.g. G-SC-1"
                  autoComplete="off"
                  className={`${baseInput} ${
                    errors.counterCode ? errRing : okRing
                  }`}
                />
                {errors.counterCode?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.counterCode.message}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  {...register("description")}
                  placeholder="Optional notes about location, showcase, etc."
                  className={`${baseInput} ${okRing} resize-y`}
                />
              </div>

              {/* Active toggle */}
              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-[13px] font-medium text-gray-700">
                  <input type="checkbox" {...register("isActive")} />
                  <span>Active</span>
                </label>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step === 2) handleSubmit(onSubmit)();
                else nextStep();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
              title={
                step === 2
                  ? mode === "edit"
                    ? "Update (Ctrl+S)"
                    : "Submit (Ctrl+S)"
                  : "Next (Ctrl+S)"
              }
            >
              {loading
                ? mode === "edit"
                  ? "Updating..."
                  : "Saving..."
                : step === 2
                ? mode === "edit"
                  ? "Update (Ctrl+S)"
                  : "Submit (Ctrl+S)"
                : "Next"}
            </motion.button>

            <Link
              to="/organisation/counter-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default UpsertCounter;
